// src/pages/HomeView/components/HomeHeader.tsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { authStorage } from '../../../lib/storage';
import customFieldsService from '../../../services/customFieldsService';
import { config } from '../../../config/env';
import CustomFields from '../../../components/CustomFields/CustomFields';
import { infoWindowService } from '../../../services/infoWindowService';
import { readViewCache, writeViewCache } from '@/lib/viewCache';

interface HomeHeaderProps {
  viewAsPersonId?: string;
  onUserLoaded?: (userName: string, imageUrl: string) => void;
}

export const HomeHeader: React.FC<HomeHeaderProps> = ({ viewAsPersonId, onUserLoaded }) =>
{
  // Only cache for own profile, not when viewing others
  const shouldUseCache = !viewAsPersonId;
  const cacheKey = 'C2_Cache_Home_Header';

  // Read cache once for initial state (only for own profile)
  const cachedHeader = useMemo(() =>
  {
    if (!shouldUseCache) return null;

    return readViewCache<Record<string, any>>(cacheKey);
  }, [shouldUseCache]);

  const [userName, setUserName] = useState<string>(cachedHeader?.userName || '');
  const [imageProfileSource, setImageProfileSource] = useState<string>(
    cachedHeader?.imageProfileSource || '',
  );
  const [imageLoadError, setImageLoadError] = useState(false);

  useEffect(() =>
  {
    if (imageProfileSource)
    {
      setImageLoadError(false);
    }
  }, [imageProfileSource]);
  const [layerCustomFields, setLayerCustomFields] = useState<any[]>(
    cachedHeader?.layerCustomFields || [],
  );
  const [personIdNum, setPersonIdNum] = useState<number>(cachedHeader?.personIdNum || 0);
  const [loading, setLoading] = useState(!cachedHeader);
  const [showDetailsLabel, setShowDetailsLabel] = useState(false);
  const [showAllDetails, setShowAllDetails] = useState(false);
  const hasLoadedRef = useRef(false);
  const customFieldsWrapperRef = useRef<HTMLDivElement>(null);
  const customFieldsInnerRef = useRef<HTMLDivElement>(null);

  useEffect(() =>
  {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    loadUserProfile();
  }, [viewAsPersonId]);

  // Fail-open strategy: Default to TRUE visibility
  useEffect(() =>
  {
    if (layerCustomFields.length > 0)
    {
      // Always start visible to ensure accessibility
      setShowDetailsLabel(true);
    }
  }, [layerCustomFields]);

  // Robust overflow detection on the wrapper
  useEffect(() =>
  {
    if (layerCustomFields.length === 0) return;

    const checkOverflow = () =>
    {
      const wrapper = customFieldsWrapperRef.current;
      if (!wrapper) return;

      // If content is truncated, scrollHeight will be greater than clientHeight
      // Use a small buffer (1px) to avoid precision errors
      const isOverflowing = wrapper.scrollHeight > wrapper.clientHeight + 1;

      // Calculate total fields across all layers to handle single-layer scenarios
      const totalFields = layerCustomFields.reduce((acc, layer) =>
      {
        return acc + (layer.CustomFields ? layer.CustomFields.length : 0);
      }, 0);

      // Heuristic: If we have many fields (e.g. > 8), likely needs expansion regardless of height calc
      const hasManyFields = totalFields > 8;

      // ONLY hide if we are sure it fits and heuristics don't demand showing it
      if (!isOverflowing && !hasManyFields)
      {
        setShowDetailsLabel(false);
      }
    };

    const wrapper = customFieldsWrapperRef.current;
    if (!wrapper) return;

    // Initial check
    checkOverflow();

    const resizeObserver = new ResizeObserver(() =>
    {
      checkOverflow();
    });

    resizeObserver.observe(wrapper);

    // observe inner too to trigger wrapper update?
    if (customFieldsInnerRef.current)
    {
      resizeObserver.observe(customFieldsInnerRef.current);
    }

    // Backup check
    const timeout = setTimeout(checkOverflow, 1000);

    return () =>
    {
      resizeObserver.disconnect();
      clearTimeout(timeout);
    };
  }, [layerCustomFields]);

  const loadUserProfile = async () =>
  {
    try
    {
      // Only show loading skeleton if we have no cached data
      setLoading(cachedHeader ? false : true);

      // Use viewAsPersonId if provided, otherwise use current user
      const personId = viewAsPersonId || authStorage.getPersonId() || '';

      if (!personId || personId === '' || personId === 'null' || personId === 'undefined')
      {
        setLoading(false);
        return;
      }

      const parsedPersonId = parseInt(personId, 10);
      if (isNaN(parsedPersonId) || parsedPersonId <= 0)
      {
        setLoading(false);
        return;
      }

      setPersonIdNum(parsedPersonId);

      const apiUrl = config.api.authUrl || 'http://localhost/Corgee.Web/';
      const token = authStorage.getAccessToken() || '';

      if (!token)
      {
        setLoading(false);
        return;
      }

      let resolvedCustomFields = layerCustomFields;
      let resolvedUserName = 'User';
      let resolvedImage = imageProfileSource;

      // When viewing as another user, load their person conversation card
      if (viewAsPersonId)
      {
        try
        {
          const personCard = await infoWindowService.getConversationById(
            0, // conversationId = 0 for person cards
            0, // layersToHide
            true, // getAllReplies
            false, // isConversationDetailsView
            0, // totalReplies
            true, // isEnabled
            true, // isPersonConversation
            parsedPersonId, // homeProfilePersonId
          );

          // Extract name from Title
          if (personCard && personCard.Title)
          {
            resolvedUserName = personCard.Title;
            setUserName(personCard.Title);
          }

          // Extract image and remove "Thumbnails/" for full size
          if (personCard && personCard.ImageName)
          {
            let imageUrl = personCard.ImageName;
            imageUrl = imageUrl.replace('/Thumbnails/', '/');
            resolvedImage = imageUrl;
            setImageProfileSource(imageUrl);
          }

          // Notify parent component with user data
          if (onUserLoaded && resolvedUserName && resolvedImage)
          {
            onUserLoaded(resolvedUserName, resolvedImage);
          }
        }
        catch (personCardError)
        {
          // Silent fail - show fallback UI
        }
      }

      // Load custom fields for the person
      try
      {
        const layersData = await customFieldsService.getPersonCustomFieldsByItemId(
          apiUrl,
          token,
          parsedPersonId,
          false,
        );

        if (layersData && Array.isArray(layersData) && layersData.length > 0)
        {
          // Search for Name and ImageName fields across all layers
          let nameField = null;
          let imageField = null;

          for (const layer of layersData)
          {
            if (layer.CustomFields && Array.isArray(layer.CustomFields))
            {
              if (!nameField)
              {
                nameField = layer.CustomFields.find(f => f.Name === 'Name');
              }
              if (!imageField)
              {
                imageField = layer.CustomFields.find(f => f.Name === 'ImageName');
              }
            }
          }

          // Extract Name field
          if (nameField && nameField.Value)
          {
            resolvedUserName = String(nameField.Value);
            setUserName(resolvedUserName);
          }
          else if (!viewAsPersonId)
          {
            // Fallback to authStorage only if viewing own profile
            const givenName = authStorage.getGivenName() || '';
            const username = authStorage.getUsername() || '';
            resolvedUserName = givenName || username || 'User';
            setUserName(resolvedUserName);
          }

          // Extract ImageName field and remove "Thumbnails/" for full size image
          if (imageField && imageField.Value)
          {
            let imageUrl = String(imageField.Value);
            imageUrl = imageUrl.replace('/Thumbnails/', '/');
            resolvedImage = imageUrl;
            setImageProfileSource(imageUrl);
          }
          else if (!viewAsPersonId)
          {
            // Fallback to authStorage only if viewing own profile
            const imageProfile = authStorage.getImageProfile();
            if (imageProfile)
            {
              resolvedImage = imageProfile;
              setImageProfileSource(imageProfile);
            }
          }

          const sorted = [...layersData]
            .filter(l => l.LayerId !== 1)
            .sort((a, b) => a.LayerId - b.LayerId);
          setLayerCustomFields(sorted);
          resolvedCustomFields = sorted;
        }
      }
      catch (customFieldsError)
      {
        console.error('Could not load person custom fields:', customFieldsError);
      }

      // Save to cache (only for own profile)
      if (shouldUseCache)
      {
        writeViewCache(cacheKey, {
          userName: resolvedUserName,
          imageProfileSource: resolvedImage,
          layerCustomFields: resolvedCustomFields,
          personIdNum: parsedPersonId,
        });
      }
    }
    catch (error)
    {
      console.error('Error loading user profile:', error);
    }
    finally
    {
      setLoading(false);
    }
  };

  const handleCustomFieldsUpdated = useCallback((updatedFields: any[], _conversation: any) =>
  {
    setLayerCustomFields(updatedFields);

    // Update cache with new field values (only for own profile)
    if (shouldUseCache)
    {
      const currentCache = readViewCache<Record<string, any>>(cacheKey) || {};

      writeViewCache(cacheKey, {
        ...currentCache,
        layerCustomFields: updatedFields,
      });
    }
  }, [shouldUseCache]);

  const customFieldsConfig = useMemo(
    () => ({
      apiUrl: config.api.authUrl || '',
      authToken: authStorage.getAccessToken() || '',
      canEdit: true,
      callApi: false,
      showHeader: false,
      disableAutoSave: false,
      UpdateDetailsConversationReact: handleCustomFieldsUpdated,
    }),
    [handleCustomFieldsUpdated],
  );

  // Todos los layers visibles en sLayers (sin split)
  const customFieldsItem = useMemo(
    () => ({
      ConversationId: 0,
      PersonId: personIdNum,
      layerCustomFields: layerCustomFields,
      ConversationLayers: layerCustomFields
        .filter((l: any) => l.LayerId !== 0)
        .map((l: any) => ({ LayerId: l.LayerId })),
    }),
    [personIdNum, layerCustomFields],
  );

  if (loading)
  {
    return (
      <div className="bg-white h-[130px] min-h-[128px] animate-pulse">
        <div className="flex items-center p-5 gap-4">
          <div className="w-[100px] h-[100px] rounded-full bg-gray-200" />
          <div className="flex-1 space-y-2">
            <div className="h-6 bg-gray-200 rounded w-1/3" />
            <div className="h-4 bg-gray-200 rounded w-1/4" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`.home-cf-wrapper .corgee_com_card-see-more-details { display: none !important; }`}</style>
      <div
        style={{ backgroundColor: '#f9f9f9' }}
        className={`home-header-root w-full min-h-[128px] relative font-['Roboto',Arial,sans-serif] transition-all duration-300 ${
          showAllDetails ? 'h-auto pb-6' : ''
        }`}
      >
        <div className="flex max-w-full">
          {/* Left Section: Photo and Basic Info */}
          <div className="flex flex-none items-start">
            <div className="w-[128px] h-[130px] flex items-center justify-center flex-none">
              {imageProfileSource && !imageLoadError ? (
                <img
                  src={imageProfileSource}
                  alt={userName}
                  style={{ margin: '15px', display: 'block', width: '100px', borderRadius: 50 }}
                  onError={() => setImageLoadError(true)}
                />
              ) : (
                <div className="w-[100px] h-[100px] rounded-full bg-[#5dade2] flex items-center justify-center text-white text-3xl font-semibold">
                  {userName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            <div className="p-[20px_16px_10px] w-[300px] flex-none">
              <div className="text-[20px] leading-[28.57px] text-[#414042] mb-1">{userName}</div>
              <div className="text-[12px] leading-[17.14px] text-[#414042] h-[48px] overflow-hidden">
                Home profile
              </div>
            </div>
          </div>

          {/* Right Section: Custom Fields */}
          {layerCustomFields.length > 0 && (
            <div
              ref={customFieldsWrapperRef}
              className={`home-cf-wrapper flex-1 min-w-0 max-w-[calc(100%-450px)] transition-all duration-300 ${
                !showAllDetails ? 'max-h-[70px] overflow-hidden' : ''
              }`}
            >
              <div ref={customFieldsInnerRef}>
                <CustomFields
                  config={customFieldsConfig}
                  item={customFieldsItem}
                  configValueId={15}
                  projectId={null}
                  isProjectTitle={true}
                />
              </div>
            </div>
          )}
        </div>

        {/* Toggle Button */}
        {showDetailsLabel && (
          <div className="absolute bottom-1 left-[450px] pl-2">
            <button
              onClick={() => setShowAllDetails(!showAllDetails)}
              className="text-[#428bca] text-[11px] leading-[15.71px] cursor-pointer hover:underline font-medium"
            >
              {showAllDetails ? 'Show Less Details' : 'Show More Details'}
            </button>
          </div>
        )}
      </div>
    </>
  );
};
